import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, MapPin, User, Search, LogOut, Truck, CheckCircle2, Heart, ShoppingBag, Share2, Cake } from "lucide-react";
import PupBirthdayForm from "@/components/PupBirthdayForm";
import { windowLabel } from "@/components/CheckoutPostcode";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import WishlistGrid from "@/components/WishlistGrid";
import { useSeo } from "@/lib/seo";
import api, { formatApiError } from "@/lib/api";
import { formatAUD } from "@/lib/media";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const StatusPill = ({ status }) => {
  const map = { paid: ["#7ED957", "Paid"], initiated: ["#FFD93D", "Awaiting payment"], expired: ["#FF6B9D", "Expired"] };
  const [c, label] = map[status] || ["#4CC9F0", status];
  return <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: `${c}33`, color: "#19324A" }}>{label}</span>;
};

const FULFIL_STEPS = ["Order placed", "In production", "Shipped", "Delivered"];
const fulfilStep = (s) => {
  const st = (s || "").toLowerCase();
  if (["delivered", "fulfilled"].includes(st)) return 3;
  if (["shipped", "in_transit", "partial"].includes(st)) return 2;
  if (["in_production", "inprocess", "printed", "printing", "passed"].includes(st)) return 1;
  return 0;
};
const trackingLinks = (order) =>
  (order.tracking || []).filter((t) => t.tracking_number || t.tracking_url)
    .map((t) => ({ code: t.tracking_number, url: t.tracking_url, carrier: [t.carrier, t.service].filter(Boolean).join(" ") }));

const ArrivalNote = ({ order }) => {
  const est = order.delivery_estimate;
  if (!est?.earliest || order.delivered_at) return null;
  return (
    <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#FFF9EE] text-[#19324A] text-xs font-semibold px-3 py-1.5" data-testid={`arrival-${order.order_number}`}>
      📦 Arriving {windowLabel(est)} to {order.postcode || est.postcode}
    </p>
  );
};

const FulfillmentTimeline = ({ order }) => {
  const s = order.fulfillment_status;
  if (!s || s === "not_required" || s === "processing") return null;
  const failed = ["failed", "canceled", "cancelled", "on_hold", "returned"].includes((s || "").toLowerCase());
  const active = fulfilStep(s);
  const links = trackingLinks(order);
  return (
    <div className="mt-4 border-t border-[#19324A]/10 pt-4" data-testid={`fulfillment-${order.order_number}`}>
      <p className="text-xs font-bold text-[#19324A]/50 mb-3 uppercase tracking-wide">Fulfillment</p>
      {failed ? (
        <p className="text-sm font-semibold text-[#FF6B9D]">There's a hold on this order ({s.replace(/_/g, " ")}). We're on it — reach out anytime.</p>
      ) : (
        <div className="flex items-center">
          {FULFIL_STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <div className="flex flex-col items-center text-center min-w-[60px]">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${i <= active ? "bg-[#7ED957] text-[#19324A]" : "bg-[#19324A]/10 text-[#19324A]/40"}`}>
                  {i < active ? <CheckCircle2 className="w-4 h-4" /> : i === 2 ? <Truck className="w-4 h-4" /> : i === 3 ? <CheckCircle2 className="w-4 h-4" /> : <Package className="w-4 h-4" />}
                </div>
                <span className={`text-[10px] mt-1 font-semibold ${i <= active ? "text-[#19324A]" : "text-[#19324A]/40"}`}>{label}</span>
              </div>
              {i < FULFIL_STEPS.length - 1 && <div className={`flex-1 h-1 rounded-full mx-1 ${i < active ? "bg-[#7ED957]" : "bg-[#19324A]/10"}`} />}
            </React.Fragment>
          ))}
        </div>
      )}
      {links.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {links.map((l, i) => (
            l.url
              ? <a key={l.url || l.code} href={l.url} target="_blank" rel="noreferrer" className="text-xs font-bold text-[#4CC9F0] underline" data-testid="tracking-link">Track parcel{l.carrier ? ` · ${l.carrier}` : ""}{l.code ? ` (${l.code})` : ""}</a>
              : <span key={l.code || l.url} className="text-xs text-[#19324A]/60">Tracking: {l.code}{l.carrier ? ` · ${l.carrier}` : ""}</span>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Account() {
  useSeo("My Account", "Manage your orders, addresses and profile.");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [trackNum, setTrackNum] = useState("");
  const [tracked, setTracked] = useState(null);
  const [wishlistItems, setWishlistItems] = useState([]);
  const { wishlist, moveManyToCart } = useStore();

  const handleMoveAll = () => {
    const n = moveManyToCart(wishlistItems);
    if (n > 0) { toast.success(`${n} item${n > 1 ? "s" : ""} moved to your bag 🐾`); setWishlistItems([]); }
    else toast.info("Pick a size/colour on your saved items first.");
  };

  const handleShare = async () => {
    try {
      const { data } = await api.post("/wishlist/share");
      await navigator.clipboard.writeText(`${window.location.origin}/wishlist/shared/${data.token}`);
      toast.success("Share link copied — send it to family for gifting! 💛");
    } catch { toast.error("Couldn't create a share link. Please try again."); }
  };

  useEffect(() => {
    if (user) api.get("/orders").then(({ data }) => setOrders(data.orders)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user) api.get("/wishlist").then(({ data }) => setWishlistItems(data.items || [])).catch(() => {});
  }, [user, wishlist.length]);

  const track = async (e) => {
    e.preventDefault();
    setTracked(null);
    try { const { data } = await api.get(`/orders/track/${trackNum.trim()}`, { params: user?.email ? { email: user.email } : {} }); setTracked(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (user === null) return <div className="max-w-4xl mx-auto px-4 py-24"><div className="h-64 bg-white/60 rounded-3xl animate-pulse" /></div>;
  if (user === false) return (
    <div className="max-w-md mx-auto px-4 py-24 text-center">
      <h1 className="font-display font-extrabold text-3xl text-[#19324A]">Your Account</h1>
      <p className="text-[#19324A]/60 mt-3">Log in to see your orders, addresses and saved items.</p>
      <div className="flex gap-3 justify-center mt-6"><Link to="/login" className="btn-pink">Log In</Link><Link to="/register" className="btn-outline">Create Account</Link></div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#19324A]">Hi, {user.name?.split(" ")[0]} 🐾</h1>
          <p className="text-[#19324A]/60">{user.email}</p>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} className="btn-outline !py-2.5" data-testid="logout-btn"><LogOut className="w-4 h-4" /> Log Out</button>
      </div>

      <Tabs defaultValue="orders">
        <TabsList className="bg-white rounded-full p-1 soft-shadow flex-wrap h-auto">
          <TabsTrigger value="orders" className="rounded-full data-[state=active]:bg-[#FFD93D] font-display" data-testid="tab-orders"><Package className="w-4 h-4 mr-1" />Orders</TabsTrigger>
          <TabsTrigger value="wishlist" className="rounded-full data-[state=active]:bg-[#FFD93D] font-display" data-testid="tab-wishlist"><Heart className="w-4 h-4 mr-1" />Wishlist ({wishlist.length})</TabsTrigger>
          <TabsTrigger value="track" className="rounded-full data-[state=active]:bg-[#FFD93D] font-display" data-testid="tab-track"><Search className="w-4 h-4 mr-1" />Track</TabsTrigger>
          <TabsTrigger value="addresses" className="rounded-full data-[state=active]:bg-[#FFD93D] font-display" data-testid="tab-addresses"><MapPin className="w-4 h-4 mr-1" />Addresses</TabsTrigger>
          <TabsTrigger value="profile" className="rounded-full data-[state=active]:bg-[#FFD93D] font-display" data-testid="tab-profile"><User className="w-4 h-4 mr-1" />Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-6">
          {orders.length === 0 ? (
            <div className="bg-white rounded-3xl soft-shadow p-10 text-center"><p className="font-display font-bold text-xl text-[#19324A]">No orders yet.</p><Link to="/shop" className="btn-primary mt-4 inline-flex">Start Shopping</Link></div>
          ) : orders.map((o) => (
            <div key={o.id} className="bg-white rounded-3xl soft-shadow p-5 mb-4" data-testid={`order-${o.order_number}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div><span className="font-display font-bold text-[#19324A]">Order {o.order_number}</span><span className="text-sm text-[#19324A]/50 ml-3">{new Date(o.created_at).toLocaleDateString("en-AU")}</span></div>
                <StatusPill status={o.payment_status} />
              </div>
              <div className="flex gap-2 mt-4 flex-wrap">{o.items.map((it) => <img key={`${it.product_id || it.slug}-${it.size || ""}-${it.colour || ""}`} src={it.image} alt={it.name} className="w-14 h-14 rounded-xl object-cover bg-[#FFF9EE]" />)}</div>
              <div className="mt-4 font-display font-bold text-[#19324A]">Total: {formatAUD(o.subtotal)}</div>
              <ArrivalNote order={o} />
              <FulfillmentTimeline order={o} />
            </div>
          ))}
        </TabsContent>

        <TabsContent value="wishlist" className="mt-6">
          {wishlistItems.length === 0 ? (
            <div className="bg-white rounded-3xl soft-shadow p-10 text-center" data-testid="wishlist-empty">
              <Heart className="w-12 h-12 text-[#FF6B9D] mx-auto" />
              <p className="font-display font-bold text-xl text-[#19324A] mt-3">No saved items yet</p>
              <p className="text-[#19324A]/60 mt-1 text-sm">Tap the heart on any product to save it here.</p>
              <Link to="/shop" className="btn-pink mt-5 inline-flex">Browse Products</Link>
            </div>
          ) : (
            <>
              <div className="flex gap-3 mb-5">
                <button onClick={handleMoveAll} data-testid="account-wishlist-move-all" className="btn-primary !py-2.5">
                  <ShoppingBag className="w-4 h-4" /> Move all to bag
                </button>
                <button onClick={handleShare} data-testid="account-wishlist-share" className="btn-outline !py-2.5">
                  <Share2 className="w-4 h-4" /> Share
                </button>
              </div>
              <WishlistGrid entries={wishlistItems} />
            </>
          )}
        </TabsContent>

        <TabsContent value="track" className="mt-6">
          <div className="bg-white rounded-3xl soft-shadow p-6">
            <form onSubmit={track} className="flex gap-3">
              <input value={trackNum} onChange={(e) => setTrackNum(e.target.value)} placeholder="Enter order number (e.g. HBC-AB12CD)" className="flex-1 rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 outline-none focus:border-[#FFD93D]" data-testid="track-input" />
              <button className="btn-pink" data-testid="track-btn">Track</button>
            </form>
            {tracked && (
              <div className="mt-6 border-t border-[#19324A]/10 pt-5">
                <div className="flex items-center justify-between"><span className="font-display font-bold text-[#19324A]">{tracked.order_number}</span><StatusPill status={tracked.payment_status} /></div>
                <div className="flex gap-2 mt-3 flex-wrap">{tracked.items.map((it) => <img key={`${it.product_id || it.slug}-${it.size || ""}-${it.colour || ""}`} src={it.image} alt="" className="w-14 h-14 rounded-xl object-cover bg-[#FFF9EE]" />)}</div>
                <p className="mt-3 font-semibold text-[#19324A]">Total: {formatAUD(tracked.subtotal)}</p>
                <FulfillmentTimeline order={tracked} />
                <ArrivalNote order={tracked} />
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="addresses" className="mt-6">
          <div className="bg-white rounded-3xl soft-shadow p-8 text-center text-[#19324A]/70">
            <MapPin className="w-12 h-12 text-[#4CC9F0] mx-auto" />
            <p className="font-display font-bold text-lg text-[#19324A] mt-3">No saved addresses yet</p>
            <p className="mt-1 text-sm">Your shipping address is collected securely at checkout. Saved address book coming soon.</p>
          </div>
        </TabsContent>

        <TabsContent value="profile" className="mt-6">
          <div className="bg-white rounded-3xl soft-shadow p-6 max-w-md">
            <label className="block text-sm font-semibold text-[#19324A]/70 mb-1">Name</label>
            <input value={user.name || ""} readOnly className="w-full rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 bg-[#FFF9EE]" />
            <label className="block text-sm font-semibold text-[#19324A]/70 mb-1 mt-4">Email</label>
            <input value={user.email} readOnly className="w-full rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 bg-[#FFF9EE]" />
            <Link to="/wishlist" className="btn-outline mt-6 w-full !py-2.5">View Saved Items</Link>
          </div>
          <div className="bg-white rounded-3xl soft-shadow p-6 max-w-md mt-4" data-testid="account-birthday-club">
            <h3 className="font-display font-bold text-[#19324A] inline-flex items-center gap-2"><Cake className="w-4 h-4 text-[#FF6B9D]" /> Pup Birthday Club</h3>
            <p className="text-xs text-[#19324A]/55 mt-1 mb-4">Add your bulldog's birthday and we'll email you a 15% treat code a week before the big day.</p>
            <PupBirthdayForm defaultEmail={user.email} lockEmail defaultName={(user.name || "").split(" ")[0]} compact />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
